import React, { useState, useEffect, useCallback } from "react";
import {
  Table, Form, Button, Spinner, Badge, Nav, Tab,
  Card, InputGroup, Alert,
} from "react-bootstrap";
import { BsPeople, BsShieldLock } from "react-icons/bs";
import { useToast } from "../context/ToastProvider";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../utils/permissions";

const API_URL = `${process.env.REACT_APP_API_URL}/api/admin/users`;

const ROLE_COLORS = {
  customer: "secondary",
  accountant: "info",
  administrator: "warning",
};

export default function AdminUsers() {
  const token = localStorage.getItem("token");
  const { showToast } = useToast();
  const { permissions: myPermissions, roleId: myRoleId } = useAuth();

  const canWriteUsers = myPermissions?.includes("users:write");
  const canManageRoles = myRoleId === "administrator";

  /* ---- Users state ---- */
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 15;
  const [savingUserId, setSavingUserId] = useState(null);

  /* ---- Roles state ---- */
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [rolePerms, setRolePerms] = useState([]);
  const [savingRole, setSavingRole] = useState(false);

  /* ---- Fetch users ---- */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to fetch users");
      setUsers(json.items || []);
      setTotal(json.total || 0);
    } catch (err) {
      showToast(err.message, "danger");
    } finally {
      setLoading(false);
    }
  }, [token, page, search, showToast]);

  /* ---- Fetch roles ---- */
  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const res = await fetch(`${API_URL}/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to fetch roles");
      setRoles(json.roles || []);
      setAllPermissions(json.permissions || []);
    } catch (err) {
      showToast(err.message, "danger");
    } finally {
      setRolesLoading(false);
    }
  }, [token, showToast]);

  useEffect(() => {
    if (token) fetchUsers();
  }, [token, fetchUsers]);

  useEffect(() => {
    if (token) fetchRoles();
  }, [token, fetchRoles]);

  /* ---- Change user role ---- */
  const handleRoleChange = async (userId, newRoleId) => {
    setSavingUserId(userId);
    try {
      const res = await fetch(`${API_URL}/${userId}/role`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ role_id: newRoleId }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to update role");
      showToast("Role updated successfully", "success");
      fetchUsers();
    } catch (err) {
      showToast(err.message, "danger");
    } finally {
      setSavingUserId(null);
    }
  };

  /* ---- Edit role permissions ---- */
  const startEditRole = (role) => {
    setEditingRole(role.id);
    setRolePerms([...role.permissions]);
  };

  const togglePermission = (permId) => {
    setRolePerms((prev) =>
      prev.includes(permId) ? prev.filter((p) => p !== permId) : [...prev, permId]
    );
  };

  const saveRolePermissions = async () => {
    setSavingRole(true);
    try {
      const res = await fetch(`${API_URL}/roles/${editingRole}/permissions`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ permissions: rolePerms }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to update permissions");
      showToast("Role permissions updated", "success");
      setEditingRole(null);
      fetchRoles();
    } catch (err) {
      showToast(err.message, "danger");
    } finally {
      setSavingRole(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const permissionsByCategory = allPermissions.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {});

  return (
    <div>
      <Tab.Container defaultActiveKey="users">
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="users">
              <BsPeople className="me-1" /> Users
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="roles">
              <BsShieldLock className="me-1" /> Roles & Permissions
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* ===== USERS TAB ===== */}
          <Tab.Pane eventKey="users">
            <InputGroup className="mb-3" style={{ maxWidth: 400 }}>
              <Form.Control
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              <Button variant="outline-secondary" onClick={fetchUsers}>Search</Button>
            </InputGroup>

            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
              <>
                <Table striped bordered hover responsive variant="dark">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Joined</th>
                      {canWriteUsers && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.full_name || "—"}</td>
                        <td>{user.email}</td>
                        <td>
                          <Badge bg={ROLE_COLORS[user.role_id] || "secondary"}>
                            {ROLE_LABELS[user.role_id] || user.role_id || "Customer"}
                          </Badge>
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                        {canWriteUsers && (
                          <td>
                            <Form.Select
                              size="sm"
                              value={user.role_id || "customer"}
                              disabled={savingUserId === user.id}
                              onChange={(e) => handleRoleChange(user.id, e.target.value)}
                              style={{ minWidth: 140 }}
                            >
                              {roles.map((r) => (
                                <option
                                  key={r.id}
                                  value={r.id}
                                  disabled={r.id === "administrator" && myRoleId !== "administrator"}
                                >
                                  {r.name}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                        )}
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr><td colSpan={5} className="text-center text-muted">No users found</td></tr>
                    )}
                  </tbody>
                </Table>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-center gap-2">
                    <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                    <span className="align-self-center text-white">Page {page} / {totalPages}</span>
                    <Button size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
                  </div>
                )}
              </>
            )}
          </Tab.Pane>

          {/* ===== ROLES TAB ===== */}
          <Tab.Pane eventKey="roles">
            {rolesLoading ? (
              <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
              <div className="row g-3">
                {roles.filter((r) => r.id !== "customer").map((role) => (
                  <div key={role.id} className="col-md-6 col-lg-4">
                    <Card bg="dark" text="white" className="h-100 border-secondary">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <Card.Title className="mb-0">
                              <Badge bg={ROLE_COLORS[role.id] || "secondary"} className="me-2">
                                {role.name}
                              </Badge>
                            </Card.Title>
                            <Card.Text className="text-muted small mt-1">{role.description}</Card.Text>
                          </div>
                          {canManageRoles && role.id !== "administrator" && (
                            <Button
                              size="sm"
                              variant="outline-light"
                              onClick={() => startEditRole(role)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          {role.permissions.map((p) => (
                            <Badge key={p} bg="secondary" className="fw-normal" style={{ fontSize: "0.7rem" }}>
                              {p}
                            </Badge>
                          ))}
                          {role.permissions.length === 0 && (
                            <span className="text-muted small">No permissions</span>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                ))}
              </div>
            )}

            {/* Edit role permissions modal-like panel */}
            {editingRole && (
              <Card bg="dark" text="white" className="mt-4 border-warning">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span>Edit permissions for: <strong>{editingRole}</strong></span>
                  <Button size="sm" variant="outline-secondary" onClick={() => setEditingRole(null)}>Cancel</Button>
                </Card.Header>
                <Card.Body>
                  {Object.entries(permissionsByCategory).map(([category, perms]) => (
                    <div key={category} className="mb-3">
                      <h6 className="text-warning">{category}</h6>
                      <div className="d-flex flex-wrap gap-3">
                        {perms.map((perm) => (
                          <Form.Check
                            key={perm.id}
                            type="checkbox"
                            id={`perm-${perm.id}`}
                            label={perm.name}
                            checked={rolePerms.includes(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                  <Button
                    variant="warning"
                    onClick={saveRolePermissions}
                    disabled={savingRole}
                  >
                    {savingRole ? "Saving..." : "Save Permissions"}
                  </Button>
                </Card.Body>
              </Card>
            )}

            {!canManageRoles && (
              <Alert variant="info" className="mt-3">
                Only Super Admins can edit role permissions.
              </Alert>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
}
